import React, { useCallback, useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import useFetch from '@hooks/useFetch';
import { localStorageAvailable } from '@utils/localStorage';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useChildNoteInfo from '@api/hooks/notes/useChildNoteInfo';
import { useRouter } from 'next/router';
import { evtNameCategoryMap } from '@constants/index';
import { SYLLABUS_CLICKED_KEYS } from '@constants/segment';
import { emptyObject } from '@constants/empty';
import { Spacings } from '@styles/index';
import BounceLoader from '@comp/PlusCourseCard/BounceLoader';
import debounce from 'lodash/debounce';
import { Document, Page, pdfjs } from 'react-pdf';
import { EPUB_NOTES, getNotesEndpoint, NOTES_VIEWED } from './utils';
import { EpubContext } from './Context/EpubContext';

pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

interface FullScreenProp {
  fullscreen: boolean;
}
interface IPDFContainerProps {
  setCurrentNotesUID: React.Dispatch<any>;
  setConceptPDFInfo: React.Dispatch<any>;
  conceptPDFInfo: {};
  selectedSubItemIds: {};
  setSelectedSubItemIds: React.Dispatch<any>;
}

const ReaderContainer = styled.div<FullScreenProp>`
  font-size: ${Spacings.SPACING_4B};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  transition: all 0.6s ease;
  ${(props) => !props.fullscreen && '0 0 5px rgba(0,0,0,.3);'}
`;

const ReaderWrapper = styled.div`
  position: relative;
  grid-template-columns: repeat(18, 32px);
`;

const Wrapper = styled.div`
  overflow-y: scroll;
  height: 100%;
  border: ${Spacings.SPACING_1} solid var(--color-divider);
  border-radius: ${Spacings.SPACING_2B};

  .react-pdf__Page__canvas {
    margin: 0 auto;
    width: 100% !important;
    height: 100% !important;
  }
`;

const Loader = styled.div`
  top: 50%;
  left: 10%;
  right: 10%;
  margin-top: -0.5em;
  position: absolute;
  text-align: center;
`;

const PDFContainer: React.FC<IPDFContainerProps> = ({
  setCurrentNotesUID,
  setConceptPDFInfo,
  conceptPDFInfo,
  selectedSubItemIds,
  setSelectedSubItemIds
}) => {
  const { selectedConcept } = useContext(EpubContext);
  const { data: pdfDetails = [], isLoading } = useFetch(
    getNotesEndpoint(selectedConcept?.uid, 'pdf')
  );
  const [numPages, setNumPages] = useState(null);
  const [currentPageNumber, setCurrentPageNumber] = useState(1);
  const [selectedPDF, setSelectedPDF] = useState(emptyObject);
  const [location, setLocation] = useState({});
  const [locationPageNumber, setLocationPageNumber] = useState(1);

  const {
    query: { goalUID }
  } = useRouter();
  const goalSegmentData = useGoalEventProps({ goalUID: goalUID as string });

  const { noteId, name } = selectedPDF;
  const { childNoteInfo, mutate: refetchNotes } = useChildNoteInfo(noteId, {
    revalidateOnMount: false
  });
  const { notesUrl: url } = childNoteInfo;

  const onDocumentLoadSuccess = useCallback(({ numPages: nextNumPages }) => {
    setNumPages(nextNumPages);
  }, []);

  useEffect(() => {
    refetchNotes();
  }, [noteId, refetchNotes]);

  useFirstRenderSkip(() => {
    if (!isLoading && pdfDetails && noteId) {
      let notesSegmentData = {};
      if (localStorageAvailable()) {
        notesSegmentData = JSON.parse(localStorage.getItem('notesSegmentData'));
      }
      segment.track(
        NOTES_VIEWED,
        {
          ...goalSegmentData,
          ...notesSegmentData,
          last_primary_source: EPUB_NOTES,
          last_primary_source_section: EPUB_NOTES,
          notes_title: name,
          notes_uid: String(noteId),
          note_type: 'PDF'
        },
        evtNameCategoryMap.WITHOUT_CATEGORY,
        true,
        SYLLABUS_CLICKED_KEYS
      );
      setConceptPDFInfo({
        ...conceptPDFInfo,
        [selectedConcept.uid]: pdfDetails
      });
    }
    setCurrentNotesUID(String(noteId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, pdfDetails, goalSegmentData, noteId]);

  useEffect(() => {
    if (pdfDetails && selectedConcept) {
      const filterItem = pdfDetails.find(
        (item) => item.noteId === selectedSubItemIds[selectedConcept?.uid]
      );
      if (filterItem) {
        setSelectedPDF(filterItem);
      } else if (pdfDetails[0]?.noteId) {
        setSelectedPDF(pdfDetails[0]);
        setSelectedSubItemIds({
          ...selectedSubItemIds,
          [selectedConcept.uid]: pdfDetails[0]?.noteId
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSubItemIds, pdfDetails]);

  useEffect(() => {
    if (noteId && localStorageAvailable()) {
      const pdfposition = localStorage.getItem('pdf-position');
      setLocation(pdfposition ? JSON.parse(pdfposition) : { [noteId]: 1 });
      setLocationPageNumber(location[noteId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [noteId]);

  if (!selectedConcept || !selectedConcept?.uid) return null;

  const renderLoading = () => (
    <Loader>
      <BounceLoader />
    </Loader>
  );

  const onLocationChanged = (pageNo) => {
    if (!noteId) return;
    const locationObj = { ...location, [noteId]: pageNo };
    setLocation(locationObj);
    if (localStorageAvailable())
      localStorage.setItem('pdf-position', JSON.stringify(locationObj));
  };

  const handleScroll = debounce((e) => {
    setLocationPageNumber(null);
    const { scrollTop, scrollHeight, scrollWidth } = e.target;
    const averagePageHeight = scrollHeight / numPages;
    const pageNo = Math.ceil(scrollTop / averagePageHeight) || 1;
    if (currentPageNumber !== pageNo) {
      setCurrentPageNumber(pageNo);
      onLocationChanged(pageNo);
    }
  }, 500);

  return (
    <ReaderWrapper>
      <ReaderContainer fullscreen={false}>
        <Wrapper onScroll={handleScroll}>
          {!!url && (
            <Document
              file={url}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={renderLoading}
            >
              {Array.from(new Array(numPages), (el, index) => (
                <Page
                  pageNumber={index + 1}
                  renderAnnotationLayer={false}
                  renderTextLayer={false}
                  loading=""
                  scale={3}
                  key={`page_${index + 1}`}
                  inputRef={(ref) => {
                    if (ref && index + 1 === locationPageNumber) {
                      ref.scrollIntoView();
                    }
                  }}
                />
              ))}
            </Document>
          )}
        </Wrapper>
      </ReaderContainer>
    </ReaderWrapper>
  );
};

export default PDFContainer;
